import { useState, useEffect } from "react";
import QuestionLayout from "./QuestionLayout";
import { renderStaffLines, renderClef } from "./staff/staffRender";

/* ───────────── CONSTANTS ───────────── */

const STAFF_HEIGHT = 60;
const STAFF_GAP = 50;

const TREBLE_STAFF_TOP = 40;
const BASS_STAFF_TOP = TREBLE_STAFF_TOP + STAFF_HEIGHT + STAFF_GAP;

const LEFT_MARGIN = 100;
const STAFF_WIDTH = 320;

const SYSTEM_TOP = TREBLE_STAFF_TOP;
const SYSTEM_BOTTOM = BASS_STAFF_TOP + STAFF_HEIGHT;

const BOX_SIZE = 36;

const BRACE_PATH =
  "M-12 -656c0 -352 -132 -696 -132 -1032c0 -180 36 -344 156 -484c4 -4 8 -8 8 -12c0 -8 -12 -16 -20 -16c-4 0 -8 0 -12 4c-160 184 -216 416 -216 656c0 360 140 712 140 1052c0 176 -40 344 -160 480v8v8c120 136 160 304 160 480c0 340 -140 692 -140 1052 c0 240 56 472 216 656c4 4 8 4 12 4c8 0 20 -8 20 -16c0 -4 -4 -8 -8 -12c-120 -140 -156 -304 -156 -484c0 -336 132 -680 132 -1032c0 -240 -52 -472 -208 -656c156 -184 208 -416 208 -656z";

/* ───────────── COMPONENT ───────────── */

export default function GrandStaffSetupWidget({
  config,
  value = {},
  onChange,
  mode = "edit"
}) {
  const [draft, setDraft] = useState(
    value.staffSetup || { treble: null, bass: null, brace: null }
  );

  const [activeSlot, setActiveSlot] = useState(null);

  // keep draft in sync if parent resets (review / reload)
  useEffect(() => {
    if (value.staffSetup) {
      setDraft(value.staffSetup);
    }
  }, [value.staffSetup]);

  function setSlot(slot, symbol) {
    const next = {
        ...draft,
        [slot]: symbol
    };

    setDraft(next);

    // ⬅️ report draft upward (NOT a commit)
    onChange?.({ staffSetup: next });
    }


  return (
    <QuestionLayout
      number={config.number}
      prompt={config.prompt}
      points={config.points}
    >
      <div style={{ position: "relative" }}>
        {/* SYMBOL PALETTE */}
        {activeSlot && mode === "edit" && (
          <div
            style={{
              display: "flex",
              gap: 16,
              padding: 12,
              border: "1px solid #ccc",
              borderRadius: 6,
              marginBottom: 12,
              background: "#fff",
              alignItems: "center"
            }}
          >
            {/* TREBLE CLEF */}
            <svg
              width={30}
              height={120}
              style={{ cursor: "pointer" }}
              onClick={() => setSlot(activeSlot, "treble")}
            >
              <g transform="translate(-55,30)">
                {renderClef("treble", 0)}
              </g>
            </svg>

            {/* BASS CLEF */}
            <svg
              width={30}
              height={120}
              style={{ cursor: "pointer" }}
              onClick={() => setSlot(activeSlot, "bass")}
            >
              <g transform="translate(-55,30)">
                {renderClef("bass", 0)}
              </g>
            </svg>

            {/* BRACE */}
            <svg
              width={20}
              height={100}
              style={{ cursor: "pointer" }}
              onClick={() => setSlot(activeSlot, "brace")}
            >
              <g transform="matrix(0.02,0,0,0.02,10,50)">
                <path d={BRACE_PATH} fill="#000" />
              </g>
            </svg>

            {/* CLEAR */}
            <button onClick={() => setSlot(activeSlot, null)}>
              Clear
            </button>
          </div>
        )}

        {/* STAFF */}
        <svg width={STAFF_WIDTH} height={SYSTEM_BOTTOM + 20}>
          {renderStaffLines(STAFF_WIDTH, TREBLE_STAFF_TOP)}
          {renderStaffLines(STAFF_WIDTH, BASS_STAFF_TOP)}

          {/* BARLINES */}
          <line
            x1={LEFT_MARGIN - 50}
            x2={LEFT_MARGIN - 50}
            y1={SYSTEM_TOP}
            y2={SYSTEM_BOTTOM - 20}
            stroke="#000"
            strokeWidth={1.1}
          />

          <line
            x1={STAFF_WIDTH - 4.5}
            x2={STAFF_WIDTH - 4.5}
            y1={SYSTEM_TOP}
            y2={SYSTEM_BOTTOM - 20}
            stroke="#000"
            strokeWidth={1.1}
          />

          <line
            x1={STAFF_WIDTH - 1}
            x2={STAFF_WIDTH - 1}
            y1={SYSTEM_TOP}
            y2={SYSTEM_BOTTOM - 20}
            stroke="#000"
            strokeWidth={3}
          />

          {/* TREBLE SLOT */}
          <rect
            x={LEFT_MARGIN - BOX_SIZE - 12.5}
            y={TREBLE_STAFF_TOP + STAFF_HEIGHT / 2 - BOX_SIZE / 2 - 10}
            width={BOX_SIZE}
            height={BOX_SIZE}
            fill="transparent"
            stroke={activeSlot === "treble" ? "#2563eb" : "#999"}
            strokeWidth={2}
            onClick={() => setActiveSlot("treble")}
          />

          {draft.treble === "treble" &&
            renderClef("treble", TREBLE_STAFF_TOP)}
          {draft.treble === "bass" &&
            renderClef("bass", TREBLE_STAFF_TOP)}
          {draft.treble === "brace" && (
            <g
              transform={`
                matrix(
                  0.02, 0, 0, 0.02,
                  ${LEFT_MARGIN - 27},
                  ${TREBLE_STAFF_TOP + 20}
                )
              `}
            >
              <path d={BRACE_PATH} fill="#000" />
            </g>
          )}

          {/* BASS SLOT */}
          <rect
            x={LEFT_MARGIN - BOX_SIZE - 12.5}
            y={BASS_STAFF_TOP + STAFF_HEIGHT / 2 - BOX_SIZE / 2 - 10}
            width={BOX_SIZE}
            height={BOX_SIZE}
            fill="transparent"
            stroke={activeSlot === "bass" ? "#2563eb" : "#999"}
            strokeWidth={2}
            onClick={() => setActiveSlot("bass")}
          />

          {draft.bass === "bass" &&
            renderClef("bass", BASS_STAFF_TOP)}
          {draft.bass === "treble" &&
            renderClef("treble", BASS_STAFF_TOP)}
          {draft.bass === "brace" && (
            <g
              transform={`
                matrix(
                  0.02, 0, 0, 0.02,
                  ${LEFT_MARGIN - 27},
                  ${BASS_STAFF_TOP + 20}
                )
              `}
            >
              <path d={BRACE_PATH} fill="#000" />
            </g>
          )}

          {/* BRACE SLOT */}
          <rect
            x={LEFT_MARGIN - BOX_SIZE - 52}
            y={
              TREBLE_STAFF_TOP +
              STAFF_HEIGHT +
              STAFF_GAP / 2 -
              BOX_SIZE / 2 - 10
            }
            width={BOX_SIZE}
            height={BOX_SIZE}
            fill="transparent"
            stroke={activeSlot === "brace" ? "#2563eb" : "#999"}
            strokeWidth={2}
            onClick={() => setActiveSlot("brace")}
          />

          {draft.brace === "brace" && (
            <g
              transform={`
                matrix(
                  0.034, 0, 0, 0.034,
                  ${LEFT_MARGIN - 55},
                  ${TREBLE_STAFF_TOP + 75}
                )
              `}
            >
              <path d={BRACE_PATH} fill="#000" />
            </g>
          )}
          {draft.brace === "bass" &&
            renderClef("bass", BASS_STAFF_TOP - 50, -40)}
          {draft.brace === "treble" &&
            renderClef("treble", BASS_STAFF_TOP - 57, -40)}
        </svg>
      </div>
    </QuestionLayout>
  );
}
